import { AppAudioPlayer, SoundEffectKeys } from "../../../audio/AppAudioPlayer";
import GameScene from "../../../scenes/GameScene";
import { RectSize } from "../../../utils/RectSize";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { OptionsDataManager } from "../../managers/OptionsDataManager";
import { AppLocalization, LanguageId } from "../../localization/AppLocalization";
import { AppSettings } from "../../managers/AppSettings";
import { LayoutDataManager } from "../../managers/LayoutDataManager";

export enum GameCompleteViewLayout {
    NONE,
    WIN,
    LOSS,
}

export class GameCompleteView extends Phaser.GameObjects.Container {
    declare scene: GameScene;

    // win
    public labelW1: Phaser.GameObjects.Text;
    public labelW2: Phaser.GameObjects.Text;
    public labelW3: Phaser.GameObjects.Text;
    // loss
    public labelL1: Phaser.GameObjects.Text;
    public labelL2: Phaser.GameObjects.Text;

    public fxWinFg: Phaser.GameObjects.Particles.ParticleEmitter;
    public fxWinBg: Phaser.GameObjects.Particles.ParticleEmitter;

    public isShowing: boolean = true;

    protected size: RectSize = new RectSize();
    protected curLayout: GameCompleteViewLayout;
    protected curLangId: LanguageId;


    constructor(parent: Phaser.GameObjects.Container) {
        super(parent.scene);

        this.Build();

        parent.add(this);
    }

    public Build(): void {
        // console.log("Build");

        this.size = new RectSize();

        this.fxWinBg = this.scene.make.particles({
            x: 500,
            y: 200,
            key: LoaderFileKeys.ui,
            config: {
                frame: "fx-glow-01.png",
                lifespan: 1600,
                frequency: 50,
                quantity: 3,
                rotate: { onEmit: () => Math.random() * 360 },
                scaleX: { start: 0, end: 2.5 },
                scaleY: { start: 0, end: 12 },
                alpha: { start: 0.4, end: 0 },
                // blendMode: Phaser.BlendModes.ADD, // seems to break with mobilePipeline and 3.60b19
                emitting: false,
            }
        });
        this.fxWinBg.scaleY = 0.5;
        this.add(this.fxWinBg);

        this.fxWinFg = this.scene.make.particles({
            x: 500,
            y: 200,
            key: LoaderFileKeys.ui,
            config: {
                // frame: "fx-glow-01.png",
                frame: "fx-sparkle-01.png",
                speedX: { onEmit: () => -600 + Math.random() * 1200 },
                speedY: { onEmit: () => -200 + Math.random() * 400 },
                // speed: { onEmit: () => -1000 + Math.random() * 2000 },
                lifespan: 800,
                quantity: 10,
                frequency: 50,
                rotate: { onEmit: () => Math.random() * 360 },
                scale: { start: 1.0, end: 0 },
                alpha: { start: 0.5, end: 1.0 },
                emitting: false,
            }
        });
        this.add(this.fxWinFg);

        this.labelW1 = this.scene.make.text({
            style: {
                color: AppSettings.colors.yellowString,
                fontSize: "60px",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                stroke: "#222",
                align: "center",
                strokeThickness: 2,
                shadow: {
                    blur: 8,
                    color: "#000",
                    fill: true,
                },
                metrics: AppSettings.metrics.montserratBold60,
            }
        });
        this.labelW1.setOrigin(0.5, 0);
        this.add(this.labelW1);

        this.labelW2 = this.scene.make.text({
            style: {
                color: AppSettings.colors.whiteString,
                fontSize: "140px",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                stroke: "#222",
                align: "center",
                strokeThickness: 4,
                shadow: {
                    blur: 16,
                    color: "#000",
                    fill: true,
                },
                metrics: AppSettings.metrics.montserratBold140,
            }
        });
        this.labelW2.setOrigin(0.5);
        this.add(this.labelW2);

        this.labelW3 = this.scene.make.text({
            style: {
                color: AppSettings.colors.whiteString,
                fontSize: "60px",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                stroke: "#222",
                align: "center",
                strokeThickness: 2,
                shadow: {
                    blur: 8,
                    color: "#000",
                    fill: true,
                },
                metrics: AppSettings.metrics.montserratBold60,
            }
        });
        this.labelW3.setOrigin(0.5, 1);
        this.add(this.labelW3);

        this.labelL1 = this.scene.make.text({
            style: {
                color: "#ec3e25",
                fontSize: "140px",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                stroke: "#222",
                align: "center",
                strokeThickness: 4,
                shadow: {
                    blur: 16,
                    color: "#000",
                    fill: true,
                },
                metrics: AppSettings.metrics.montserratBold140,
            }
        });
        this.labelL1.setOrigin(0.5, 0);
        this.add(this.labelL1);

        this.labelL2 = this.scene.make.text({
            style: {
                color: AppSettings.colors.whiteString,
                fontSize: "60px",
                fontFamily: AppSettings.fontFamilies.MontserratBold,
                stroke: "#222",
                align: "center",
                strokeThickness: 2,
                shadow: {
                    blur: 8,
                    color: "#000",
                    fill: true,
                },
                metrics: AppSettings.metrics.montserratBold60,
            }
        });
        this.labelL2.setOrigin(0.5, 1);
        this.add(this.labelL2);

        this.HideAll();
    }

    private HideAll(): void {
        this.fxWinBg.visible = false;
        this.fxWinFg.visible = false;
        this.labelW1.visible = false;
        this.labelW2.visible = false;
        this.labelW3.visible = false;
        this.labelL1.visible = false;
        this.labelL2.visible = false;
    }

    public Show(animated: boolean = true): void {
        this.ShowLayout(this.curLayout);
        this.StartLayout(this.curLayout);
        this.PlayLayoutSound(this.curLayout);

        this.UpdateFromLocalizationChange();

        this.visible = true;
        this.scene.tweens.killTweensOf([this.labelW1, this.labelW2, this.labelW3]);
        const duration = OptionsDataManager.GetTiming(1000);
        if (animated) {
            this.scene.tweens.add({
                targets: this,
                scale: {
                    from: 0,
                    to: 1,
                },
                duration: duration,
                ease: AppSettings.ease.genericShowHideEase,
            })
        }
        else {
            this.scale = 1;
        }
        this.isShowing = true;
    }

    public Hide(animated: boolean = true, onComplete?: Function): void {
        // console.log("Hide; animated=", animated);

        this.scene.tweens.killTweensOf([this.labelW1, this.labelW2, this.labelW3]);
        const duration = OptionsDataManager.GetTiming(1000);
        if (animated) {
            this.scene.tweens.add({
                targets: this,
                scale: {
                    from: 1,
                    to: 0,
                },
                duration: duration,
                ease: AppSettings.ease.genericShowHideEase,
                onComplete: () => {
                    if (this.curLayout != null) {
                        this.HideLayout(this.curLayout);
                        this.StopLayout(this.curLayout);
                    }
                    onComplete?.();
                    this.visible = false;
                }
            })
        }
        else {
            if (this.curLayout != null) {
                this.HideLayout(this.curLayout);
                this.StopLayout(this.curLayout);
            }
            this.scale = 0;
            onComplete?.();
            this.visible = false;
        }
        this.isShowing = true;
    }

    /**
     * If Layout changes, kill old layout, set and update new layout
     * @param layout 
     */
    public SetLayout(layout: GameCompleteViewLayout): void {
        if (this.curLayout != layout) {
            // console.log("SetLayout", GameCompleteViewLayout[layout]);
            this.curLayout = layout;
        }
    }

    public UpdateFromLocalizationChange(): void {
        const newLangId: LanguageId = AppLocalization.GetCurLanguageId();
        if (this.curLangId != newLangId) {
            this.labelW1.text = AppLocalization.GetSharedData().dealComplete.titleCongrats.itemLabel;
            this.labelW2.text = AppLocalization.GetSharedData().dealComplete.titleYourTeam.itemLabel;
            this.labelW3.text = AppLocalization.GetSharedData().dealComplete.titleWon.itemLabel;
            this.labelL1.text = AppLocalization.GetSharedData().dealComplete.titleGameOver.itemLabel;
            this.labelL2.text = AppLocalization.GetSharedData().dealComplete.titleTheyWon.itemLabel;
            this.curLangId = this.curLangId;
        }
    }

    public PlayLayoutSound(layout: GameCompleteViewLayout): void {
        switch (layout) {
            case GameCompleteViewLayout.WIN:
                AppAudioPlayer.PlaySfxRandomFromCollection([
                    SoundEffectKeys.win1,
                    SoundEffectKeys.win4,
                    SoundEffectKeys.win5,
                ]);
                break;
            case GameCompleteViewLayout.LOSS:
                AppAudioPlayer.PlaySfxRandomFromCollection([
                    SoundEffectKeys.loss1,
                ]);
                break;
            case GameCompleteViewLayout.NONE:
                break;
        }
    }

    public ShowLayout(layout: GameCompleteViewLayout): void {
        switch (layout) {
            case GameCompleteViewLayout.WIN:
                this.labelW1.visible = true;
                this.labelW2.visible = true;
                this.labelW3.visible = true;
                this.fxWinBg.visible = true;
                this.fxWinFg.visible = true;
                break;
            case GameCompleteViewLayout.LOSS:
                this.labelL1.visible = true;
                this.labelL2.visible = true;
                break;
            case GameCompleteViewLayout.NONE:
                break;
        }
    }

    public HideLayout(layout: GameCompleteViewLayout): void {
        switch (layout) {
            case GameCompleteViewLayout.WIN:
                this.labelW1.visible = false;
                this.labelW2.visible = false;
                this.labelW3.visible = false;
                this.fxWinBg.visible = false;
                this.fxWinFg.visible = false;
                break;
            case GameCompleteViewLayout.LOSS:
                this.labelL1.visible = false;
                this.labelL2.visible = false;
                break;
            case GameCompleteViewLayout.NONE:
                break;
        }
    }

    public StartLayout(layout: GameCompleteViewLayout): void {
        switch (layout) {
            case GameCompleteViewLayout.WIN:
                this.fxWinBg.emitting = true;
                this.fxWinFg.emitting = true;
                break;
            case GameCompleteViewLayout.LOSS:
                break;
            case GameCompleteViewLayout.NONE:
                break;
        }
    }

    public StopLayout(layout: GameCompleteViewLayout): void {
        switch (layout) {
            case GameCompleteViewLayout.WIN:
                this.fxWinBg.emitting = false;
                this.fxWinFg.emitting = false;
                break;
            case GameCompleteViewLayout.LOSS:
                break;
            case GameCompleteViewLayout.NONE:
                break;
        }
    }

    public SetWidth(width: number) {
        this.size.width = width;
    }

    /**
     * Sets @see size.height (for parent) and repositions elements for @see curLayout
     * Executes through @see SetLayout
     * @param layout 
     */
    public SetHeightByLayout(layout?: GameCompleteViewLayout): void {
        // console.log("SetHeightByLayout");
        let halfWidth: number;
        let halfHeight: number;
        let totalContentHeight: number;
        let verticalPadding: number;
        let elementSpacingY: number;
        const targetLayout = layout ?? this.curLayout;
        switch (targetLayout) {
            case GameCompleteViewLayout.WIN:
                this.size.height = LayoutDataManager.GetSharedData().dealComplete.gameCompleteView.win.height;
                elementSpacingY = LayoutDataManager.GetSharedData().dealComplete.gameCompleteView.win.elementSpacingY;
                // figure out remaining space
                halfWidth = this.size.width * 0.5;
                halfHeight = this.size.height * 0.5;
                totalContentHeight = this.labelW1.displayHeight + elementSpacingY + this.labelW2.displayHeight + elementSpacingY + this.labelW3.displayHeight;
                verticalPadding = (this.size.height - totalContentHeight) * 0.5;
                // position elements
                this.fxWinBg.setPosition(halfWidth, halfHeight);
                this.fxWinFg.setPosition(halfWidth, halfHeight);
                this.labelW1.setPosition(halfWidth, verticalPadding);
                this.labelW2.setPosition(halfWidth, halfHeight);
                this.labelW3.setPosition(halfWidth, this.size.height - verticalPadding);

                // box middle title
                this.labelW2.scale = 1;
                this.labelW2.displayWidth = Math.min(this.labelW2.displayWidth, this.size.width);
                break;
            case GameCompleteViewLayout.LOSS:
                this.size.height = LayoutDataManager.GetSharedData().dealComplete.gameCompleteView.loss.height;
                elementSpacingY = LayoutDataManager.GetSharedData().dealComplete.gameCompleteView.loss.elementSpacingY;
                // figure out remaining space
                halfWidth = this.size.width * 0.5;
                halfHeight = this.size.height * 0.5;
                totalContentHeight = this.labelL1.displayHeight + elementSpacingY + this.labelL2.displayHeight;
                verticalPadding = (this.size.height - totalContentHeight) * 0.5;
                // position elements
                this.fxWinBg.setPosition(halfWidth, halfHeight);
                this.fxWinFg.setPosition(halfWidth, halfHeight);
                this.labelL1.setPosition(halfWidth, verticalPadding);
                this.labelL2.setPosition(halfWidth, this.size.height - verticalPadding);
                
                this.labelL1.scale = 1;
                this.labelL1.displayWidth = Math.min(this.labelL1.displayWidth, this.size.width);
                break;
            case GameCompleteViewLayout.NONE:
                // parent needs this
                this.size.height = LayoutDataManager.GetSharedData().dealComplete.gameCompleteView.none.height;
                break;
        }
    }

    public GetSize(): RectSize {
        return this.size;
    }
}